import GoogleMapsLoader from 'google-maps';
import getMuiTheme from 'material-ui/styles/getMuiTheme';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import HttpStatus from 'http-status-codes';
import { asyncRequest, fetchByCompanyId, patch, TOMCAT_URL, UserRole } from 'src/common/index';
import { fetchOwnAccount } from 'src/actions/accounts';
import { fetchNewTasksSinceLastLogin } from 'src/actions/tasks';
import Notifications from 'src/components/operatingtool/notifications';
import { getOwnAccount, isLoggedInAsEvoAdmin } from 'src/selectors/accounts';
import { getOwnCompany } from 'src/selectors/bus-companies';
import { getTaskNewsSinceLastLogin } from 'src/selectors/tasks';
import Footer from 'src/components/nav/footer';
import Header from 'src/components/nav/header';
import FaviconLoader from 'src/components/misc/FaviconLoader';
import { isLoggedInWithRole } from 'src/components/account/login';
import { DragDropContext } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import Cookies from 'js-cookie';
import i18next, { LOCALE_COOKIE, LOCALE_DEBUG_MODE } from 'src/i18n';
import { saveExchangeRates } from 'src/utils/currency';
import { safeGetStorageItem, STORAGE_KEYS } from 'src/utils/storage';
import { initializeCsrfToken } from 'src/actions/api/csrf';
import { checkSessionCookies } from 'src/components/misc/cookieProbe';
import { ErrorState } from 'src/components/misc/ErrorState';
import { getRequiredRoles } from 'src/routes';

const muiTheme = getMuiTheme({
    palette: {
        primary1Color: '#0c66ab',
        accent1Color: 'darkgrey',
    },
});

class App extends Component {
    constructor(props) {
        super(props);

        this.state = {
            apiError: undefined,
            cookieError: undefined,
        };
    }

    getChildContext() {
        return { muiTheme: getMuiTheme(muiTheme) };
    }

    async componentDidMount() {
        const cookiesAccepted = await checkSessionCookies();

        if (!cookiesAccepted) {
            console.warn('Setting a session attribute failed, the session cookie was probably rejected.');
            this.setState({ cookieError: true });
            return;
        }

        // get csrf token
        await initializeCsrfToken();

        // get api version, show an error when unavailable
        this.getApiVersion();

        // get the exchange rates and store them in a cookie for 12 hours
        this.getExchangeRates();

        // check if already logged in and fetch account
        // this also triggers the auto-login on localhost if not already logged in
        await this.props.fetchOwnAccount();

        this.redirect(this.props);
    }

    componentWillReceiveProps(nextProps) {
        const { ownAccount } = nextProps;
        let previousLanguage = this.props.ownAccount.entity ? this.props.ownAccount.entity.language : undefined;
        let currentLanguage = ownAccount.entity ? ownAccount.entity.language : undefined;

        if (currentLanguage !== previousLanguage) {
            if (Cookies.get(LOCALE_COOKIE) === LOCALE_DEBUG_MODE) i18next.changeLanguage(LOCALE_DEBUG_MODE);
            else i18next.changeLanguage(currentLanguage);
        }

        if (!this.props.ownAccount.entity && ownAccount.entity) {
            // load google api
            GoogleMapsLoader.KEY = Config.GOOGLE_API_KEY;
            GoogleMapsLoader.LIBRARIES = ['places', 'geometry', 'drawing'];
            GoogleMapsLoader.LANGUAGE =
                ownAccount.entity.language && isLoggedInWithRole(ownAccount, UserRole.OPERATOR)
                    ? ownAccount.entity.language
                    : 'de';
            GoogleMapsLoader.REGION =
                ownAccount.entity.contactData.country && isLoggedInWithRole(ownAccount, UserRole.OPERATOR)
                    ? ownAccount.entity.contactData.country
                    : 'AT';
            GoogleMapsLoader.load(google => {
                // console.log('Google', google);
            });
        }

        this.redirect(nextProps);
    }

    render() {
        const { t, ownAccount, busCompany, children } = this.props;

        if (this.state.cookieError) {
            return <ErrorState title={t('error_cookie.title')} message={t('error_cookie.message')} />;
        }
        if (this.state.apiError) {
            return <ErrorState title={t('error_state.title')} message={t('error_state.message')} />;
        }

        const currentPath = children.props.location.pathname;
        const isStart = currentPath === '/' || currentPath === '/login' || currentPath === '/login-with-token';
        const isError = currentPath.startsWith('/error');

        const notFound = children.props.route.path === '*';
        return (
            <div className="container-fluid">
                <Helmet title={`${t('title')} – ${t('header_operating_tool')}`} />
                <FaviconLoader />
                <Notifications />
                <Header
                    ownAccount={ownAccount}
                    busCompany={busCompany}
                    fetchByCompanyId={this.props.fetchByCompanyId}
                    patch={this.props.patch}
                    fetchNewTasksSinceLastLogin={this.props.fetchNewTasksSinceLastLogin}
                    newTasksSinceLastLogin={this.props.newTasksSinceLastLogin}
                    isError={isError || notFound}
                />
                <br />
                {
                    // only load children if ownAccount has been successfully loaded
                    (isStart || isError || notFound || (ownAccount.entity && !ownAccount.isFetching)) && children
                }
                <Footer />
            </div>
        );
    }

    getApiVersion() {
        asyncRequest(`${TOMCAT_URL}public/version`)
            .then(response => {
                Config.API_VERSION = response.json.version;
                this.setState({ apiError: undefined });
            })
            .catch(error => this.setState({ apiError: error }));
    }

    getExchangeRates() {
        asyncRequest(`${TOMCAT_URL}public/exchange-rates`)
            .then(response => saveExchangeRates(response.json))
            .catch(error => this.setState({ apiError: error }));
    }

    redirect(props) {
        const { ownAccount, busCompany, push, children } = props;

        const currentPath = children.props.location.pathname;
        const notFound = children.props.route.path === '*';

        if (!ownAccount.isFetching && ownAccount.lastUpdated != null) {
            const role = ownAccount.entity && ownAccount.entity.role;
            const requiredRoles = getRequiredRoles(window.location.pathname);
            if (
                currentPath === '/' ||
                (!notFound &&
                    currentPath !== '/login' &&
                    currentPath !== '/login-with-token' &&
                    ((ownAccount.error && ownAccount.error.status === HttpStatus.UNAUTHORIZED) ||
                        !ownAccount.entity ||
                        isLoggedInWithRole(ownAccount, [UserRole.CUSTOMER, UserRole.DRIVER])))
            ) {
                console.log('Unauthorized or forbidden role');
                safeGetStorageItem(
                    STORAGE_KEYS.REDIRECT_URL_AFTER_LOGIN,
                    `${location.pathname}${location.search}`,
                    'session',
                );
                push('/login');
            } else if (isLoggedInAsEvoAdmin() && !window.location.pathname.startsWith('/admin')) {
                console.log('Wrong role (evoadmin):', window.location.pathname, role);
                push('/admin');
            } else if (requiredRoles && (!role || !requiredRoles.includes(role))) {
                console.log('Wrong role:', window.location.pathname, role);
                push('/');
            }
        }
    }

    getOperatorDispatcherPartnerPaths() {
        return [
            '/operating-tool',
            '/administration',
            '/task-administration',
            '/task-exchange',
            '/offer-administration',
            '/help',
        ];
    }
}

App.propTypes = {
    children: PropTypes.element,
};

App.childContextTypes = {
    muiTheme: PropTypes.object.isRequired,
};

const mapStateToProps = state => {
    return {
        ownAccount: getOwnAccount(state),
        busCompany: getOwnCompany(state),
        newTasksSinceLastLogin: getTaskNewsSinceLastLogin(state),
    };
};

export default withTranslation()(
    connect(mapStateToProps, {
        patch,
        push,
        fetchOwnAccount,
        fetchByCompanyId,
        fetchNewTasksSinceLastLogin: fetchNewTasksSinceLastLogin,
    })(DragDropContext(HTML5Backend)(App)),
);
